import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import useMedia from 'hooks/useMedia';

import CompareDropdown from 'components/Header/CompareDropdown/CompareDropdown';
import NotificationEvents from 'components/notifications/Events';

import { ReactComponent as IconSearch } from 'icons/icon-search.svg';
import { ReactComponent as OfficialChartsIcon } from 'icons/official-charts-header-nav-icon.svg';
import { ReactComponent as DSPChartsIcon } from 'icons/dsp-charts-icon.svg';
import { ReactComponent as TopListsIcon } from 'icons/fire-icon-top-nav.svg';
import { ReactComponent as PlaylistActivityIcon } from 'icons/icon-playlist-activity.svg';
import { ReactComponent as WMGLogoWithText } from 'icons/icon-wmg-logo-with-text.svg';
import { ReactComponent as WMGLogoWithTextSmall } from 'icons/icon-wmg-logo-with-text-small.svg';
import { ReactComponent as MobileMenuIcon } from 'icons/mobile-menu-icon.svg';
import { ReactComponent as InfoIcon } from 'icons/icon-info.svg';

import {
  withEntityContext,
  CompareModes,
  EntityTypes,
} from 'context/entityContext';

import {
  TopBarWrapper,
  StyledTopBar,
  HomeLink,
  TopbarLink,
  TopBarTitle,
  DefaultTopBar,
  TopBarButtons,
  StyledWMGLogo,
  HomeLinkLabel,
  WMGLogoWrapper,
  TopicBarWrapper,
  TobbarSearchLink,
  SearchIconWrapper,
  OfficialChartsIconWrapper,
  PlaylistActivityIconWrapper,
  MobileMenuIconWrapper,
  ChartsIconWrap,
  TopListsIconWrapper,
  FilterTitleWrapper,
  InfoIconWrapper,
  LogoWrapper,
} from './TopBar.style';
import { AllTopicTypes } from 'topics';
import { RootState } from 'redux/rootReducer';
import TopicFiltersTopBar from 'components/TopicFilters/TopicFiltersTopBar';
import { IChartInfoProps } from 'pages/Charts';
import ExportRawDataButton from 'components/ExportRawData/ExportRawDataButton';
import { IAuthenticationProfile } from 'redux/reducers/authentication';
import TopBarSmall from './TopBarSmall';
import TopicFiltersStickyTopBar from 'components/TopicFilters/TopicFiltersStickyTopBar';
import { FilterTitle } from 'components/TopicFilters/TopicFilters.styles';
import Box from 'components/elements/Box';
import UserDropdown from '../UserDropdown/UserDropdown';
import MobileMenu from '../MobileMenu/MobileMenu';
import { SHOW_BELL_NOTIFICATION } from 'config';
import getRuntimeEnv from 'helpers/env';

export const ALLOW_EXPORT_GROUP = getRuntimeEnv().REACT_APP_ALLOW_EXPORT_GROUP;

interface IOuterProps {
  entityTitle?: string | any;
  /** Is the Header Component in view */
  inView?: boolean;
  /** Is the topic filter bar in view */
  topicFiltersInView?: boolean;
  /** Should the Topbar be sticky or not */
  isSticky?: boolean;
  /** Are we and in which compareMode are we right now? */
  compareMode?: CompareModes;
  /** The Entity Type e.g. track, release, label */
  entityType?: EntityTypes | string;
  /** The Entity Type e.g. track, release, label or 'compare', 'DSP chart', 'Official chart' */
  topicEntityType?: EntityTypes | string;
  /** The Entity IDs in the form of an array, no further entity information is included */
  entityIds?: string[];
  /** The Country IDs in the form of an array */
  countryIds?: string[];
  /** Object containing all information for the entities in the Redux cache */
  entitiesById?: any;
  /** The current topic */
  topic?: AllTopicTypes[];
  /** scr for logo */
  artworkSrc?: string;
  /** Object containing all information for chart page */
  chartInfo?: IChartInfoProps;
  productType?: string;
  noComparedItems?: boolean;
  simpleTextTopBar?: boolean;
  gradientColor?: 'blue';
  inNewDesign?: boolean;
  isStatic?: boolean;
  showOnlyOnScroll?: boolean;
  artists?: [{ artist: any; data: any }];
  artistsFulfilled?: boolean;
}

interface ITopBarProps extends IOuterProps {
  /** Indicates if the user is logged in, used to hide the content in the TopBar */
  isAuthenticated: boolean;
  /** User data */
  userProfile: IAuthenticationProfile;
  /** to display Opus Access Banner in footer */
  isLoadedAccessBanner: boolean;
  isAccessBannerShown: boolean;
  BetaGroupPriorityPlaylist?: string[];
  PriorityPlaylistOnlyForBeta?: boolean;
  userLocation?: string | null;
  userLabel?: string[] | [];
  userRole?: string | null;
  userDivision?: string | null;
}

/**
 * The TopBar component contains the Opus logo, links to the homepage, the top of the page and
 * will eventually contain more functionality (filter/maybe compare).
 */
const TopBar: React.FC<ITopBarProps> = (props) => {
  let { topicFiltersInView } = props;
  const {
    isAccessBannerShown,
    isAuthenticated,
    isLoadedAccessBanner,
    isSticky,
    isStatic,
    inView,
    compareMode,
    countryIds,
    entityType,
    topicEntityType,
    entityIds,
    topic,
    entitiesById,
    entityTitle,
    artworkSrc,
    chartInfo,
    productType,
    userProfile,
    noComparedItems = true,
    showOnlyOnScroll,
    simpleTextTopBar,
    gradientColor,
    inNewDesign,
    artists,
    artistsFulfilled,
    BetaGroupPriorityPlaylist,
    PriorityPlaylistOnlyForBeta,
    userLocation,
    userLabel,
    userRole,
    userDivision,
  } = props;

  const [visibleDropdown, setVisibleDropdown] = useState<string>();
  const [defaultHeight, setDefaultHeight] = useState(true);
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [notificationsOpened, setNotificationsOpened] = useState(false);

  const showPlaylistActivity =
    PriorityPlaylistOnlyForBeta === false ||
    (BetaGroupPriorityPlaylist &&
      BetaGroupPriorityPlaylist.map((email: string) =>
        email.toLowerCase()
      ).includes(userProfile?.email.toLowerCase()));

  /**
   * Method that is called when the dropdown button is clicked
   * @param {string} key one of 'compareDropdown' or 'filters'
   */
  function toggleDropdown(key: string): void {
    if (visibleDropdown === key) {
      setVisibleDropdown(undefined);
    } else {
      setVisibleDropdown(key);
    }
  }

  /** Always show the default TopBar when the header is in view or the TopBar isn't sticky. Otherwise show the ScrollToTopBar */
  const defaultTopBarVisible = inView || !isSticky;
  if (topicFiltersInView === undefined) {
    topicFiltersInView = defaultTopBarVisible;
  }

  if (defaultHeight !== topicFiltersInView) {
    setDefaultHeight(topicFiltersInView);
  }

  const isSmallScreen = useMedia('(max-width: 50em)');
  const isMobileScreen = useMedia('(max-width: 43.9375em)');
  const isTopLists = topic && topic[0] === 'topLists';
  const topBarIsVisible =
    defaultTopBarVisible || (isSmallScreen && entityTitle === 'Home');

  return (
    <TopBarWrapper
      isSticky={isSticky}
      isStatic={isStatic}
      isHomePage={entityTitle === 'Home'}
      isMobileScreen={isMobileScreen}
      data-pw="top-bar-wrapper"
      defaultHeight={defaultHeight}
      isVisible={showOnlyOnScroll ? !topicFiltersInView : true}
    >
      <StyledTopBar
        gradientColor={gradientColor}
        isMobileScreen={isMobileScreen}
        isHomePage={entityTitle === 'Home'}
        isAuthenticated={isAuthenticated}
        isStatic={isStatic}
        isVisible={
          showOnlyOnScroll
            ? !topicFiltersInView && (!isSmallScreen || entityTitle === 'Home')
            : isSmallScreen
            ? defaultTopBarVisible || entityTitle === 'Home' || isTopLists
            : true
        }
      >
        <TopBarTitle>
          <h2>Main navigation</h2>
          <DefaultTopBar
            isStatic={isStatic}
            isHomePage={entityTitle === 'Home'}
            isMobileScreen={isMobileScreen}
            isVisible
          >
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              pl={isMobileScreen ? '10px' : '0'}
              pr={isMobileScreen ? '15px' : '0'}
              ml={isSmallScreen ? 1 : 0}
            >
              {isStatic ? (
                <LogoWrapper isMobileScreen={isMobileScreen}>
                  <HomeLink
                    withoutScale
                    withoutOnHover
                    to="/"
                    isAuthenticated={isAuthenticated}
                  >
                    {isMobileScreen ? (
                      <WMGLogoWithTextSmall />
                    ) : (
                      <WMGLogoWithText />
                    )}
                  </HomeLink>
                </LogoWrapper>
              ) : (
                <HomeLink withoutScale to="/" isAuthenticated={isAuthenticated}>
                  <WMGLogoWrapper isAuthenticated={isAuthenticated}>
                    <StyledWMGLogo />
                  </WMGLogoWrapper>
                  <HomeLinkLabel>
                    {isAuthenticated ? '' : 'Welcome to Opus'}
                  </HomeLinkLabel>
                </HomeLink>
              )}
              {isTopLists && isSmallScreen && (
                <FilterTitleWrapper topicFiltersInView={topicFiltersInView}>
                  <FilterTitle>WHAT’S TRENDING</FilterTitle>
                </FilterTitleWrapper>
              )}
            </Box>
          </DefaultTopBar>
        </TopBarTitle>
        <TopBarButtons
          isMobileScreen={isMobileScreen}
          isVisible={topBarIsVisible}
          isStatic={isStatic || topBarIsVisible}
        >
          {isAuthenticated && (
            <>
              {userProfile &&
                userProfile.groups &&
                ALLOW_EXPORT_GROUP &&
                userProfile.groups.includes(ALLOW_EXPORT_GROUP) && (
                  <ExportRawDataButton userProfile={userProfile} />
                )}

              {!isMobileScreen && showPlaylistActivity && (
                <PlaylistActivityIconWrapper data-pw="playlist-activity">
                  <TopbarLink to="/playlistActivity">
                    <PlaylistActivityIcon />
                  </TopbarLink>
                </PlaylistActivityIconWrapper>
              )}

              {!isMobileScreen && (
                <TopListsIconWrapper data-pw="top-lists">
                  <TopbarLink to="/toplists">
                    <TopListsIcon />
                  </TopbarLink>
                </TopListsIconWrapper>
              )}

              {entityType !== 'label' && !isMobileScreen && (
                <CompareDropdown
                  toggleDropdown={(): void => toggleDropdown('compareDropdown')}
                  isOpen={visibleDropdown === 'compareDropdown'}
                  entityIds={entityIds}
                  entityType={entityType}
                  topic={topic}
                  compareMode={compareMode}
                  entitiesById={entitiesById}
                />
              )}

              {!isMobileScreen && (
                <ChartsIconWrap data-pw="dsp">
                  <TopbarLink to="/chart/dsp">
                    <DSPChartsIcon />
                  </TopbarLink>
                </ChartsIconWrap>
              )}

              {!isMobileScreen && (
                <OfficialChartsIconWrapper data-pw="official">
                  <TopbarLink to="/chart/official">
                    <OfficialChartsIcon />
                  </TopbarLink>
                </OfficialChartsIconWrapper>
              )}

              {isMobileScreen && isStatic ? (
                <>
                  <TobbarSearchLink>
                    <TopbarLink
                      to="/search"
                      isActive={
                        entityTitle === 'Search' &&
                        !mobileMenuOpened &&
                        !notificationsOpened
                      }
                      roundHover
                    >
                      <SearchIconWrapper>
                        <IconSearch data-pw="top-bar-search" />
                      </SearchIconWrapper>
                    </TopbarLink>
                  </TobbarSearchLink>
                  {!!SHOW_BELL_NOTIFICATION && (
                    <NotificationEvents
                      setNotificationsOpened={setNotificationsOpened}
                    />
                  )}
                </>
              ) : (
                <>
                  {!!SHOW_BELL_NOTIFICATION && (
                    <NotificationEvents
                      setNotificationsOpened={setNotificationsOpened}
                    />
                  )}
                  <TobbarSearchLink>
                    <TopbarLink to="/search">
                      <SearchIconWrapper>
                        <IconSearch data-pw="top-bar-search" />
                      </SearchIconWrapper>
                    </TopbarLink>
                  </TobbarSearchLink>
                </>
              )}

              <InfoIconWrapper>
                <TopbarLink to="/help">
                  <InfoIcon data-pw="help" />
                </TopbarLink>
              </InfoIconWrapper>

              <UserDropdown
                toggleDropdown={(): void => toggleDropdown('userDropdown')}
                isOpen={visibleDropdown === 'userDropdown'}
                isStatic={isStatic}
                isAccessBannerShown={
                  isLoadedAccessBanner &&
                  (isAccessBannerShown === undefined ||
                    isAccessBannerShown === null)
                }
                userProfile={userProfile}
                userLocation={userLocation}
                userLabel={userLabel}
                userRole={userRole}
                userDivision={userDivision}
                isMobileScreen={isMobileScreen}
              />

              {isMobileScreen && (
                <>
                  <MobileMenuIconWrapper
                    isActive={mobileMenuOpened}
                    onClick={(): void => setMobileMenuOpened(!mobileMenuOpened)}
                  >
                    <MobileMenuIcon data-pw="mobile-menu-icon" />
                  </MobileMenuIconWrapper>
                  {mobileMenuOpened && (
                    <MobileMenu
                      toggleDropdown={(): void =>
                        setMobileMenuOpened(!mobileMenuOpened)
                      }
                      entityIds={entityIds}
                      entityType={entityType}
                      topic={topic}
                      compareMode={compareMode}
                      entitiesById={entitiesById}
                      showPlaylistActivity={showPlaylistActivity}
                    />
                  )}
                </>
              )}
            </>
          )}
        </TopBarButtons>
      </StyledTopBar>
      {
        <TopicBarWrapper
          isSmall={simpleTextTopBar}
          isVisible={!topicFiltersInView}
          className={
            !topicFiltersInView
              ? `visible ${!simpleTextTopBar && inNewDesign && 'top-lists'}`
              : null
          }
          inNewDesign={inNewDesign}
          gradientColor={gradientColor}
          isSmallScreen={isSmallScreen}
          fixedHeight={entityTitle === 'Home'}
          isTopLists={isTopLists}
        >
          {!simpleTextTopBar ? (
            !inNewDesign ? (
              <TopicFiltersTopBar
                topic={topic}
                entityType={topicEntityType || entityType}
                entityIds={entityIds}
                entityTitle={entityTitle}
                artworkSrc={artworkSrc}
                chartInfo={chartInfo}
                productType={productType}
                entitiesById={entitiesById}
                countryIds={countryIds}
                compareMode={!noComparedItems ? compareMode : undefined}
                isViewRelatedMenuShown={!compareMode}
                artists={artists}
                artistsFulfilled={artistsFulfilled}
              />
            ) : (
              <TopicFiltersStickyTopBar
                topic={topic}
                entityType={topicEntityType || entityType}
              />
            )
          ) : (
            <TopBarSmall
              isSmallScreen={isSmallScreen}
              title={entityTitle}
              simpleTextTopBar={simpleTextTopBar}
            />
          )}
        </TopicBarWrapper>
      }
    </TopBarWrapper>
  );
};

TopBar.defaultProps = {
  inView: true,
  isSticky: false,
  inNewDesign: false,
};

const mapStateToProps = (
  state: RootState
): {
  isAuthenticated: boolean;
  userProfile: IAuthenticationProfile;
  isAccessBannerShown: boolean;
  isLoadedAccessBanner: boolean;
  userLocation?: string | null;
  userLabel?: string[] | [];
  userRole?: string | null;
  userDivision?: string | null;
  BetaGroupPriorityPlaylist?: string[];
  PriorityPlaylistOnlyForBeta?: boolean;
} => ({
  isAuthenticated: state.authentication.isAuthenticated,
  userProfile: state.authentication.profile,
  isAccessBannerShown: state.userSettings?.data?.opusAccessFooterBanner,
  userLocation: state.userSettings?.data?.userLocation,
  userLabel: state.userSettings?.data?.userLabel,
  userRole: state.userSettings?.data?.userRole,
  userDivision: state.userSettings?.data?.userDivision,
  isLoadedAccessBanner:
    state.userSettings?.loading?.all === false &&
    state.userSettings?.success?.all === true,
  BetaGroupPriorityPlaylist:
    state.consulEnvData?.data?.BetaGroupPriorityPlaylist,
  PriorityPlaylistOnlyForBeta:
    state.consulEnvData?.data?.PriorityPlaylistOnlyForBeta,
});

export default compose<ITopBarProps, IOuterProps>(
  withEntityContext,
  connect(mapStateToProps)
)(TopBar);
